import React, { useRef, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import ThemeContext from '../../contexts/ThemeContext';
import SearchSelect from '../SearchSelect';
import Label from '../Label';
import DeleteIcon from '../../images/DeleteIcon';
import UploadIcon from '../../images/UploadIcon';
import Icon from '../Icon';
import { Section, HelpText } from '../PrebookingSharedComponents';
import { ErrorText } from './shared';

import i18n from '../../i18n';
import { formatFileSize } from '../../utils/formatters';
import TruncatedFileName from '../TruncatedFileName';
import {
  TRANSLATION_MAX_SIZE,
  TRANSLATION_ACCEPT_STRING,
  TRANSLATION_ALLOWED_EXTENSIONS,
  getTranslationAllowedTypesDisplayString,
  isTranslationAllowedFileType,
  TRANSLATION_EXTENSION_TO_MIME_MAP,
  isTranslationSignatureValid,
} from '../../config/attachmentConfig';

const FileUploadArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BrowseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 20px;
  border: 1px solid ${({ $colors }) => $colors.borderColorMedium};
  background-color: white;
  border-radius: 20px;
  color: ${({ $colors }) => $colors.twilight};
  font-size: ${({ $layout }) => $layout.mediumFontSize}px;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  align-self: flex-start;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  &:hover {
    border-color: ${({ $colors }) => $colors.twilight};
    background-color: ${({ $colors }) => $colors.hoverColor};
  }
`;

const AcceptedTypesText = styled.p`
  font-size: ${({ $layout }) => $layout.smallFontSize}px;
  font-weight: 500;
  color: ${({ $colors }) => $colors.heroPurple};
  margin: 4px 0 0 0;
  line-height: 1.2;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 8px;
`;

const FileItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const FileNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const FileName = styled(TruncatedFileName)`
  font-size: ${({ $layout }) => $layout.mediumFontSize}px;
  font-weight: 500;
  color: ${({ $colors }) => $colors.twilight};
`;

const DeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid ${({ $colors }) => $colors.borderColorMedium};
  background: white;
  border-radius: 20px;
  color: ${({ $colors }) => $colors.twilight};
  font-size: ${({ $layout }) => $layout.smallFontSize}px;
  font-weight: 500;
  letter-spacing: 0.15px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    border-color: ${({ $colors }) => $colors.errorColor};
    color: ${({ $colors }) => $colors.errorColor};
    background-color: rgba(234, 126, 143, 0.05);
  }
`;

const ScopeRow = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ScopeFieldWrapper = styled.div`
  flex: 1;
`;

const ScopeInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid ${({ hasError, $colors }) => hasError ? ($colors.errorColor) : $colors.webDefaultInputBorderColor};
  border-radius: 4px;
  font-size: ${({ $layout }) => $layout.mediumFontSize}px;
  font-weight: 500;
  background-color: ${({ $colors }) => $colors.webLightGray};
  color: ${({ $colors }) => $colors.webDarkBlue};
  box-sizing: border-box;
  height: 40px;

  &:focus {
    outline: none;
    border-color: ${({ $colors }) => $colors.twilight};
  }

  &::placeholder {
    color: ${({ $colors }) => $colors.placeholderColor};
  }
`;

const ScopeTypeWrapper = styled.div`
  flex: 1;
`;

const getScopeTypeOptions = () => [
  { value: 'words', label: i18n('translation_order_scope_words') },
  { value: 'pages', label: i18n('translation_order_scope_pages') },
  // Backend unit type is 'seconds', shown as "Minutes" to users
  { value: 'seconds', label: i18n('translation_order_scope_minutes') },
];

const readFileSignature = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target.result);
      const hex = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
      resolve(hex);
    };
    reader.onerror = () => resolve('');
    reader.readAsArrayBuffer(file.slice(0, 16));
  });

const getFileExtension = (filename) => {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filename.substring(lastDot).toLowerCase();
};

const FileUpload = ({
  attachments,
  errors,
  addAttachment,
  removeAttachment,
  updateAttachmentScope,
  setErrors,
}) => {
  const { colors, layout } = useContext(ThemeContext);
  const fileInputRef = useRef(null);
  const scopeTypeOptions = getScopeTypeOptions();

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    const fileErrors = [];
    let hasValidFile = false;

    for (const file of files) {
      if (file.size > TRANSLATION_MAX_SIZE) {
        fileErrors.push(`${file.name}: ${i18n('attachment_too_large') || 'File too large'} (max ${formatFileSize(TRANSLATION_MAX_SIZE)})`);
        continue;
      }

      // Validate extension (use last extension only to catch double-extension tricks)
      const ext = getFileExtension(file.name);
      if (!ext || !TRANSLATION_ALLOWED_EXTENSIONS.includes(ext)) {
        fileErrors.push(`${file.name}: ${i18n('attachment_type_not_allowed') || 'File type not allowed'}`);
        continue;
      }

      // Prefer the canonical MIME for the (already validated) extension over the
      // browser-reported file.type, which is unreliable for audio formats
      // (e.g. .m4a is often reported as audio/x-m4a, which fails the allow-list).
      const mimeType = TRANSLATION_EXTENSION_TO_MIME_MAP[ext] || file.type || '';

      if (!isTranslationAllowedFileType(mimeType)) {
        fileErrors.push(`${file.name}: ${i18n('attachment_type_not_allowed') || 'File type not allowed'}`);
        continue;
      }

      const signatureHex = await readFileSignature(file);
      if (!isTranslationSignatureValid(signatureHex, mimeType, ext)) {
        fileErrors.push(`${file.name}: ${i18n('attachment_invalid_signature') || 'Invalid file'}`);
        continue;
      }

      addAttachment(file);
      hasValidFile = true;
    }

    setErrors(prev => {
      const next = { ...prev };
      if (fileErrors.length > 0) {
        next.fileUpload = fileErrors.join('; ');
      } else {
        delete next.fileUpload;
      }
      if (hasValidFile) {
        delete next.attachments;
      }
      return next;
    });

    e.target.value = '';
  };

  return (
    <>
      {/* Upload reference document */}
      <Section>
        <Label text={i18n('translation_order_upload_reference')} required />
        <FileUploadArea>
          <BrowseButton $colors={colors} $layout={layout} onClick={() => fileInputRef.current?.click()}>
            <UploadIcon color={colors.twilight} size={18} />
            {i18n('translation_order_browse_files')}
          </BrowseButton>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={TRANSLATION_ACCEPT_STRING}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <AcceptedTypesText $colors={colors} $layout={layout}>
            {i18n('translation_order_accepted_types')}: {getTranslationAllowedTypesDisplayString()}. Max {formatFileSize(TRANSLATION_MAX_SIZE)}.
          </AcceptedTypesText>
          {errors.fileUpload && <ErrorText>{errors.fileUpload}</ErrorText>}
          {errors.attachments && <ErrorText>{i18n(errors.attachments)}</ErrorText>}
        </FileUploadArea>
      </Section>

      {/* Uploaded files list */}
      {attachments.length > 0 && (
        <FileList>
          {attachments.map((att, index) => (
            <FileItemContainer key={att.id}>
              <FileHeader>
                <FileNameRow>
                  <Icon icon="attach_file" iconLib="material" iconColor={colors.twilight} iconSize={18} withoutContainer={true} />
                  <FileName $colors={colors} $layout={layout} filename={att.filename} fileSize={att.size} />
                </FileNameRow>
                <DeleteButton $colors={colors} $layout={layout} onClick={() => removeAttachment(att.id)} title={i18n('remove')}>
                  <DeleteIcon color={colors.twilight} size={16} />
                  {i18n('delete') || 'Delete'}
                </DeleteButton>
              </FileHeader>
              <ScopeRow>
                <ScopeFieldWrapper>
                  <Label text={i18n('translation_order_estimated_scope')} required />
                  <ScopeInput
                    $colors={colors}
                    $layout={layout}
                    type="number"
                    min="1"
                    value={att.estimatedScope}
                    hasError={errors.attachmentErrors?.[index]?.estimatedScope}
                    onChange={(e) => {
                      updateAttachmentScope(att.id, 'estimatedScope', e.target.value);
                      setErrors(prev => {
                        const next = { ...prev };
                        if (next.attachmentErrors?.[index]) {
                          delete next.attachmentErrors[index].estimatedScope;
                        }
                        return next;
                      });
                    }}
                  />
                  {errors.attachmentErrors?.[index]?.estimatedScope && (
                    <ErrorText>{i18n(errors.attachmentErrors[index].estimatedScope)}</ErrorText>
                  )}
                </ScopeFieldWrapper>
                <ScopeTypeWrapper>
                  <Label text={i18n('translation_order_select_type')} required />
                  <SearchSelect
                    selected={att.scopeType ? scopeTypeOptions.find(o => o.value === att.scopeType) : null}
                    onChange={(option) => {
                      updateAttachmentScope(att.id, 'scopeType', option?.value || null);
                      setErrors(prev => {
                        const next = { ...prev };
                        if (next.attachmentErrors?.[index]) {
                          delete next.attachmentErrors[index].scopeType;
                        }
                        return next;
                      });
                    }}
                    selectableItems={scopeTypeOptions}
                    placeholder={i18n('translation_order_select_type')}
                    customHeight="40px"
                    customBackgroundColor={colors.webLightGray}
                    customBorderColor={colors.webDefaultInputBorderColor}
                  />
                  {errors.attachmentErrors?.[index]?.scopeType && (
                    <ErrorText>{i18n(errors.attachmentErrors[index].scopeType)}</ErrorText>
                  )}
                </ScopeTypeWrapper>
              </ScopeRow>
            </FileItemContainer>
          ))}
        </FileList>
      )}

      {/* Scope help text — only shown when files are uploaded */}
      {attachments.length > 0 && (
        <HelpText style={{ color: colors.heroPurple }}>
          {i18n('translation_order_scope_help_text')}
        </HelpText>
      )}
    </>
  );
};

FileUpload.propTypes = {
  attachments: PropTypes.array.isRequired,
  errors: PropTypes.object.isRequired,
  addAttachment: PropTypes.func.isRequired,
  removeAttachment: PropTypes.func.isRequired,
  updateAttachmentScope: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
};

export default FileUpload;
